import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { AlertTriangle, ChevronLeft, ChevronRight, ClipboardList, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { AssignmentMonthGrid, AssignmentEntriesList } from "../components/AssignmentBoardParts";
import { AssignJudgeDialog } from "../components/AssignJudgeDialog";

const MONTHS = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];
const pad = (n) => String(n).padStart(2, "0");

export default function AssignmentBoard() {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (t) => privacy ? anonymize(t) : t;
  const isAdmin = user?.role === "admin";
  const userCourtTypes = (user?.court_types && user.court_types.length) ? user.court_types : ["Μονομελές", "Πολυμελές"];
  const courtTypeOptions = isAdmin ? ["Μονομελές", "Πολυμελές"] : userCourtTypes;
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [courtType, setCourtType] = useState(() => courtTypeOptions.length === 1 ? courtTypeOptions[0] : "");
  const [sector, setSector] = useState("");
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [data, setData] = useState({ entries: [], sectors: [], total_entries: 0, total_cases: 0, unassigned_cases: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [target, setTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const dateFrom = `${cursor.y}-${pad(cursor.m + 1)}-01`;
  const dateTo = `${cursor.y}-${pad(cursor.m + 1)}-${pad(new Date(cursor.y, cursor.m + 1, 0).getDate())}`;

  useEffect(() => {
    axios.get(`${API}/assignment-board/branches`).then(r => setBranches(r.data?.branches || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        if (courtType) params.append("court_type", courtType);
        if (branch) params.append("branch", branch);
        if (sector) params.append("sector_number", sector);
        if (!onlyUnassigned) params.append("only_unassigned", "false");
        const r = await axios.get(`${API}/assignment-board?${params.toString()}`, { timeout: 120000 });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης πίνακα προσδιορισμού");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, courtType, branch, sector, onlyUnassigned, reloadKey]);

  useEffect(() => { setSelectedDate(null); }, [dateFrom, courtType, branch, sector]);

  const shownEntries = useMemo(
    () => selectedDate ? data.entries.filter(e => e.hearing_date === selectedDate) : data.entries,
    [data.entries, selectedDate]
  );
  const shiftMonth = (delta) => setCursor(({ y, m }) => { const d = new Date(y, m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const sectorLabel = sector ? (data.sectors.find(s => String(s.sector_number) === sector)?.title || `Τομέας ${sector}`) : "Όλα τα αντικείμενα";
  const filterLabel = [branch || "Όλα τα καταστήματα", courtType || "Μονομελές & Πολυμελές", sectorLabel].join(" · ");
  const sectorOptions = useMemo(() => {
    const seen = new Map();
    (data.sectors || []).forEach(s => { if (!seen.has(s.sector_number)) seen.set(s.sector_number, s); });
    return [...seen.values()];
  }, [data.sectors]);

  const openAssignCase = (entry, c) => setTarget({
    caseIds: [c.id], courtType: c.court_type || entry.court_type, composition: entry.composition,
    pinakioLabel: entry.pinakio_label, hearingDate: entry.hearing_date,
    title: `ΓΑΚ ${c.gak}${c.eak ? ` · ΕΑΚ ${c.eak}` : ""}`, subtitle: `${entry.pinakio_label} — ${entry.hearing_date.split("-").reverse().join("/")}${c.assigned_judge ? ` · τώρα: ${c.assigned_judge}` : ""}`,
  });
  const openAssignAll = (entry, ids) => setTarget({
    caseIds: ids, courtType: entry.court_type, composition: entry.composition,
    pinakioLabel: entry.pinakio_label, hearingDate: entry.hearing_date,
    title: `${entry.pinakio_label} — ${entry.hearing_date.split("-").reverse().join("/")}`, subtitle: `Όλες οι υποθέσεις χωρίς δικαστή (${ids.length})`,
  });
  const reload = () => setReloadKey(k => k + 1);

  return (
    <div className="space-y-4" data-testid="assignment-board-page">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-5 h-5 text-red-700" />Προσδιορισμός</CardTitle>
          <CardDescription>Πινάκια και δικάσιμοι με υποθέσεις που δεν έχουν ακόμη δικαστή προδικαστικού ελέγχου. Κλικ σε υπόθεση για ορισμό δικαστή. Πολυμελές: πρώτα «Ανάθεση σε Σύνθεση», μετά ορισμός δικαστή-μέλους της σύνθεσης.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftMonth(-1)} data-testid="board-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="font-semibold text-sm min-w-[170px] text-center" data-testid="board-month-label">{MONTHS[cursor.m]} {cursor.y}</span>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftMonth(1)} data-testid="board-next-month"><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} data-testid="board-today-btn">Σήμερα</Button>
            </div>
            <Select value={branch || "__all__"} onValueChange={(v) => setBranch(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 w-52 text-xs" data-testid="board-branch-filter"><SelectValue placeholder="Όλα τα καταστήματα" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__all__">Όλα τα καταστήματα</SelectItem>
                {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={courtType || "__all__"} onValueChange={(v) => { setCourtType(v === "__all__" ? "" : v); setSector(""); }} disabled={courtTypeOptions.length === 1}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="board-court-type-filter"><SelectValue placeholder="Μονομελές & Πολυμελές" /></SelectTrigger>
              <SelectContent>
                {courtTypeOptions.length > 1 && <SelectItem value="__all__">Μονομελές & Πολυμελές</SelectItem>}
                {courtTypeOptions.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sector || "__all__"} onValueChange={(v) => setSector(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 w-72 text-xs" data-testid="board-sector-filter"><SelectValue placeholder="Όλα τα αντικείμενα" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__all__">Όλα τα αντικείμενα</SelectItem>
                {sectorOptions.map(s => <SelectItem key={s.sector_number} value={String(s.sector_number)}>{s.sector_number}ο — {s.short_title || s.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
                <>
                  <Badge variant="outline" className="border-slate-300 text-slate-700" data-testid="board-total-entries">{data.total_entries} πινάκια/δικάσιμοι</Badge>
                  <Badge className={`border-0 text-white ${data.unassigned_cases ? "bg-red-600" : "bg-emerald-600"}`} data-testid="board-unassigned-cases">
                    {data.unassigned_cases ? <AlertTriangle className="w-3 h-3 mr-1" /> : null}{data.unassigned_cases} υποθ. χωρίς δικαστή
                  </Badge>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <p className="text-xs text-slate-500" data-testid="board-filter-label">Φίλτρο: <b>{filterLabel}</b></p>
            <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
              <Checkbox checked={onlyUnassigned} onCheckedChange={(v) => setOnlyUnassigned(!!v)} data-testid="board-only-unassigned" />
              Μόνο πινάκια με υποθέσεις <span className="text-red-700 font-semibold">χωρίς δικαστή</span>
            </label>
          </div>
        </CardContent>
      </Card>

      <AssignmentMonthGrid year={cursor.y} month={cursor.m} entries={data.entries} selectedDate={selectedDate} onSelectDate={setSelectedDate} />

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          {selectedDate ? `Δικάσιμοι ${selectedDate.split("-").reverse().join("/")}` : `Όλες οι δικάσιμοι — ${MONTHS[cursor.m]} ${cursor.y}`}
        </h3>
        {selectedDate && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedDate(null)} data-testid="board-clear-day">Όλος ο μήνας</Button>}
      </div>
      <AssignmentEntriesList entries={shownEntries} privacyFn={p} onAssignCase={openAssignCase} onAssignAll={openAssignAll} onReload={reload} />

      <AssignJudgeDialog target={target} onClose={() => setTarget(null)} onDone={reload} />
    </div>
  );
}
